import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import config from '../../config.js';
import UserContext from '../common/UserContext.js';

// components
import SortableLinkCell from './SortableLinkCell.js';
import {convertDateFormat} from '../common/Grid.js';
import NotesHtmlCell from './NotesHtmlCell.js';

// utilities
import {createSurveyScheduler} from "../common/utilities.js";

// kendo react
import {getSelectedState, Grid, GridColumn} from '@progress/kendo-react-grid';
import {Button} from '@progress/kendo-react-buttons';
import {orderBy} from '@progress/kendo-data-query';
import {getter} from '@progress/kendo-react-common';

// multilingual
import {useLocalization} from '@progress/kendo-react-intl';
import {Text} from '../common/MultilingualText.js';
import {
    descriptionKey,
    limitDownloadThenRequestKey,
    browserLimitConcurrentDownloadKey,
    limitsMultipleConcurrentDownloadsKey,
    downloadSelectedKey,
    fileSizeKey,
    mainMessages,
    notesKey,
    platformKey,
    releaseDateKey
} from '../../assets/text/MultilingualText.js';
import KSMPopover from "../common/Popovers";
import {useSupportContext} from "../common/SupportContext";

const DATA_ITEM_KEY = "update_item_id";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);


function SoftwareFirmwareGrid(props) {
    const {
        software,
        setIsLoading
    } = props;
    const {
        accessToken,
        siteLanguageDefault,
        surveyTimerRef,
        surveyCompletedRef,
        setSurveyState,
        globalConfig
    } = useContext(UserContext);
    const { dispatch } = useSupportContext();
    const localization = useLocalization();

    // survey scheduler
    const scheduleSurvey = createSurveyScheduler({
        surveyTimerRef,
        surveyCompletedRef,
        setSurveyState,
        globalConfig
    });

    let items = software;
    const alertAnchor = useRef(null);

    const SoftwareUpdatesDescription = localization.toLanguageString(descriptionKey, mainMessages[siteLanguageDefault][descriptionKey]);
    const SoftwareUpdatesPlatform = localization.toLanguageString(platformKey, mainMessages[siteLanguageDefault][platformKey]);
    const SoftwareUpdatesNotes = localization.toLanguageString(notesKey, mainMessages[siteLanguageDefault][notesKey]);
    const SoftwareUpdatesReleaseDate = localization.toLanguageString(releaseDateKey, mainMessages[siteLanguageDefault][releaseDateKey]);
    const SoftwareUpdatesFileSize = localization.toLanguageString(fileSizeKey, mainMessages[siteLanguageDefault][fileSizeKey]);
    const SoftwareUpdatesDownloadSelected = localization.toLanguageString(downloadSelectedKey, mainMessages[siteLanguageDefault][downloadSelectedKey]);
    const SoftwareUpdatesDownloadMessagesInfo = localization.toLanguageString(limitsMultipleConcurrentDownloadsKey, mainMessages[siteLanguageDefault][limitsMultipleConcurrentDownloadsKey])
    const SoftwareUpdatesDownloadMessagesTitle = localization.toLanguageString(browserLimitConcurrentDownloadKey, mainMessages[siteLanguageDefault][browserLimitConcurrentDownloadKey])
    const SoftwareUpdatesDownloadMessagesBody = localization.toLanguageString(limitDownloadThenRequestKey, mainMessages[siteLanguageDefault][limitDownloadThenRequestKey])

    const initialSort = [
        {
            field: "Description",
            dir: "asc"
        }
    ];
    const [alertShow, setAlertShow] = useState(false);
    const dataState = items.map((dataItem) =>
        Object.assign(
            {
                selected: false
            },
            {
                ...dataItem,
                ReleaseDate: convertDateFormat(typeof dataItem['ReleaseDate'] === 'string' ? dataItem['ReleaseDate'].replace(/-/g, "/") : dataItem['ReleaseDate'])
            }
        )
    );

    const [selectedState, setSelectedState] = useState({});
    const [downloadDisabled, setDownloadDisabled] = useState(true);
    const onSelectionChange = useCallback((event) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: selectedState,
            dataItemKey: DATA_ITEM_KEY,
        });
        setSelectedState(newSelectedState);
    }, [selectedState]);

    const onHeaderSelectionChange = useCallback((event) => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = {};
        event.dataItems.forEach((item) => {
            newSelectedState[idGetter(item)] = checked;
        });
        setSelectedState(newSelectedState);
    }, []);

    useEffect(() => {
        if (Object.values(selectedState).includes(true)) {
            setDownloadDisabled(false);
        } else {
            setDownloadDisabled(true);
        }
    }, [selectedState]);

    const onSoftwareDownload = async () => {
        try {
            setIsLoading(true);

            const headers = {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + accessToken
            };

            const params = new URL(document.location).searchParams;
            const updateId = params.get('uuid');

            const urls = [];
            const update_item_ids = [];
            const itemMetadata = [];

            // support metadata items
            items.forEach((item) => {
                const id = item.update_item_id;
                if (selectedState[id]) {
                    urls.push(item.url);
                    update_item_ids.push(id);
                }
                itemMetadata.push({
                    title: item?.Description,
                    size: item?.FileSize,
                    type: item?.Platform
                });
            });

            // set support metadata context for download items
            dispatch({
                type: 'UPDATE_TRANSACTION',
                payload: { download_item: itemMetadata }
            });

            const response = await axios.post(
                config.software_updates.GET_SOFTWARE_UPDATE_URL,
                {
                    urls,
                    update_item_ids,
                    update_id: updateId
                },
                { headers }
            );

            const computedUrls = response.data || [];

            for (let i = 0; i < computedUrls.length; i++) {
                await new Promise(r => setTimeout(r, i * 4000));
                await downloadFile(computedUrls[i]);
            }

        } catch (error) {
            console.log("ERROR: Failed to get presigned url", error);
        } finally {
            setIsLoading(false);

            // start timer to show software updates survey
            scheduleSurvey({
                module: 'software_updates',
                data: [new URL(document.location).searchParams.get('uuid')],
            });
        }
    };

    /*
    * downloadFile() downloads files from url
    * @param {url} string url of download path/link
    */
    const downloadFile = async (url) => {
        return new Promise((res, rej) => {
            try {
                let a = document.createElement("a");
                a.setAttribute('href', url);
                a.setAttribute('download', '');
                a.setAttribute('target', '_blank');
                a.click();
                res(true);
            } catch (error) {
                console.log('err', error);
                rej(error);
            }
        });
    }

    return (
        <div>
            <Grid
                className={'software-updates-software-firmware-grid k-mb-2'}
                data={orderBy(dataState.map((item) => ({
                    ...item,
                    [SELECTED_FIELD]: selectedState[idGetter(item)],
                    "Description": JSON.stringify({
                        "text": item['Description'],
                        "link": `${item['url']}`,
                        "accessToken": accessToken
                    })
                })), initialSort)}
                scrollable={'none'}
                dataItemKey={DATA_ITEM_KEY}
                selectedField={SELECTED_FIELD}
                onSelectionChange={onSelectionChange}
                onHeaderSelectionChange={onHeaderSelectionChange}
            >
                <GridColumn
                    field={SELECTED_FIELD}
                    width="40px"
                    headerSelectionValue={dataState.findIndex((item) => !selectedState[idGetter(item)]) === -1}
                />
                <GridColumn
                    field="Description"
                    title={SoftwareUpdatesDescription}
                    cell={(props) =>
                        <SortableLinkCell
                            setIsLoading={setIsLoading}
                            props={props}
                        />
                    }
                />
                <GridColumn
                    field="Platform"
                    title={SoftwareUpdatesPlatform}
                />
                <GridColumn
                    field="Notes"
                    title={SoftwareUpdatesNotes}
                    cell={NotesHtmlCell}
                />
                <GridColumn
                    field="ReleaseDate"
                    title={SoftwareUpdatesReleaseDate}
                />
                <GridColumn
                    field="FileSize"
                    title={SoftwareUpdatesFileSize}
                />
            </Grid>
            <Button
                className={'k-mb-4'}
                themeColor={"primary"}
                size={"large"}
                fillMode={"solid"}
                type={"button"}
                name="Search"
                onClick={onSoftwareDownload}
                disabled={downloadDisabled}
            >
                {SoftwareUpdatesDownloadSelected}
            </Button>
            {Object.values(selectedState).filter((x) => x === true).length >= 7 && (
                <>
                    <button
                        ref={alertAnchor}
                        className={"ksm-anchor-button"}
                        style={{
                            marginLeft: '0.938rem'
                        }}
                        onMouseOver={() => {
                            setAlertShow(true);
                        }}
                        onMouseLeave={() => {
                            setAlertShow(false);
                        }}>
                        <Text
                            textkey={limitsMultipleConcurrentDownloadsKey}
                            textdefault={SoftwareUpdatesDownloadMessagesInfo}
                        />
                    </button>
                    {alertShow && (
                        <KSMPopover
                            show={alertShow}
                            anchor={alertAnchor.current}
                            position={'right'}
                            type={'warning'}
                            message={
                                <div>
                                    <b>{SoftwareUpdatesDownloadMessagesTitle}</b>
                                    <br/>
                                    {SoftwareUpdatesDownloadMessagesBody}
                                </div>
                            }
                        />
                    )}
                </>
            )}
        </div>
    );
}

export default SoftwareFirmwareGrid;